/**
 * Frontend LLM client — thin wrapper over the backend Ni² LLM
 * router. Two shapes:
 *   • streamNi2Chat({sessionId, messages, activeCaseId, onDelta,
 *     onTools, onDone, onError}) → returns an abort function.
 *   • synthesizeMM({mmId, caseId, transcriptSegments, ...}) →
 *     Promise<{synthesis}>.
 *
 * All calls use REACT_APP_BACKEND_URL. No API keys ever live in the
 * browser.
 */

const API = () => `${process.env.REACT_APP_BACKEND_URL}/api/llm`;

export function streamNi2Chat({ sessionId, messages, activeCaseId, activeImagingId, activeImagingPlaceholder, scope, currentUserId, onDelta, onTools, onProgress, onDone, onError }) {
  const ctrl = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API()}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          messages,
          active_case_id: activeCaseId,
          active_imaging_id: activeImagingId || null,
          active_imaging_placeholder: activeImagingPlaceholder || null,
          scope: scope || "personal",
          current_user_id: currentUserId || "dr_rao",
        }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => "");
        throw new Error(`Stream failed (${res.status}): ${errText.slice(0, 200)}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split by SSE frame boundary
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const frame of parts) {
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const raw = line.slice(5).trim();
          if (!raw) continue;
          let payload;
          try { payload = JSON.parse(raw); } catch { continue; }
          if (payload.type === "delta") onDelta?.(payload.content || "");
          else if (payload.type === "tools") onTools?.(payload.tools || []);
          else if (payload.type === "progress") onProgress?.(payload);
          else if (payload.type === "done") { onDone?.(); return; }
          else if (payload.type === "error") { onError?.(payload.error || "LLM error"); return; }
        }
      }
      onDone?.();
    } catch (e) {
      if (e.name !== "AbortError") onError?.(e.message || String(e));
    }
  })();
  return () => ctrl.abort();
}

export async function synthesizeMM(payload) {
  const res = await fetch(`${API()}/mm/synthesize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      mm_id: payload.mmId,
      case_id: payload.caseId,
      transcript_segments: payload.transcriptSegments || [],
      collaboration_records: payload.collaborationRecords || [],
      demo_pins: payload.demoPins || [],
      demo_imaging_comments: payload.demoImagingComments || [],
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `MM synthesis failed (${res.status})`);
  }
  return res.json();
}
